// src/integrations/onedrive.ts

type GraphErr = { error?: { code?: string; message?: string } };

// caracteres proibidos no OneDrive/SharePoint: " * : < > ? / \ |
function safeFolderName(s: string): string {
  const raw = String(s ?? "").trim();
  const cleaned = raw
    .replace(/[\"*:<>?\/\\|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || "CASE").slice(0, 120);
}

async function graphJson(
  accessToken: string,
  url: string,
  options?: RequestInit
) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(options?.headers || {}),
    },
  });

  const data = (await res.json().catch(() => ({}))) as any;
  return { res, data };
}

async function ensureChildFolderByName(
  accessToken: string,
  parentItemId: string,
  folderName: string
): Promise<{ id: string; name: string; existed: boolean }> {
  const listUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${parentItemId}/children?$select=id,name,folder`;
  const { res: resList, data: dataList } = await graphJson(
    accessToken,
    listUrl,
    { method: "GET" }
  );

  if (!resList.ok) {
    const e = dataList as GraphErr;
    throw new Error(
      e?.error?.message || `Graph list children HTTP ${resList.status}`
    );
  }

  const found = Array.isArray(dataList?.value)
    ? dataList.value.find(
        (x: any) =>
          x?.folder &&
          typeof x?.name === "string" &&
          x.name.toUpperCase() === folderName.toUpperCase()
      )
    : null;

  if (found?.id) {
    return { id: found.id, name: found.name, existed: true };
  }

  const createUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${parentItemId}/children`;
  const { res: resCreate, data: dataCreate } = await graphJson(
    accessToken,
    createUrl,
    {
      method: "POST",
      body: JSON.stringify({
        name: folderName,
        folder: {},
        "@microsoft.graph.conflictBehavior": "fail",
      }),
    }
  );

  if (!resCreate.ok) {
    const e = dataCreate as GraphErr;
    if (e?.error?.code === "nameAlreadyExists") {
      return ensureChildFolderByName(accessToken, parentItemId, folderName);
    }
    throw new Error(
      e?.error?.message || `Graph create folder HTTP ${resCreate.status}`
    );
  }

  return {
    id: dataCreate?.id,
    name: dataCreate?.name || folderName,
    existed: false,
  };
}

async function ensurePathInRoot(
  accessToken: string,
  pathSegments: string[]
): Promise<{ id: string; path: string }> {
  let currentId = "root";
  const built: string[] = [];

  for (const seg of pathSegments) {
    const name = safeFolderName(seg);

    if (currentId === "root") {
      const listUrl =
        "https://graph.microsoft.com/v1.0/me/drive/root/children?$select=id,name,folder";
      const { res: resList, data: dataList } = await graphJson(
        accessToken,
        listUrl,
        { method: "GET" }
      );

      if (!resList.ok) {
        const e = dataList as GraphErr;
        throw new Error(
          e?.error?.message || `Graph list root HTTP ${resList.status}`
        );
      }

      const found = Array.isArray(dataList?.value)
        ? dataList.value.find(
            (x: any) =>
              x?.folder &&
              typeof x?.name === "string" &&
              x.name.toUpperCase() === name.toUpperCase()
          )
        : null;

      if (found?.id) {
        currentId = found.id;
        built.push(found.name || name);
        continue;
      }

      const createUrl =
        "https://graph.microsoft.com/v1.0/me/drive/root/children";
      const { res: resCreate, data: dataCreate } = await graphJson(
        accessToken,
        createUrl,
        {
          method: "POST",
          body: JSON.stringify({
            name,
            folder: {},
            "@microsoft.graph.conflictBehavior": "fail",
          }),
        }
      );

      if (!resCreate.ok) {
        const e = dataCreate as GraphErr;
        if (e?.error?.code === "nameAlreadyExists") continue;
        throw new Error(
          e?.error?.message ||
            `Graph create root folder HTTP ${resCreate.status}`
        );
      }

      currentId = dataCreate?.id;
      built.push(dataCreate?.name || name);
      continue;
    }

    const ensured = await ensureChildFolderByName(accessToken, currentId, name);
    currentId = ensured.id;
    built.push(ensured.name || name);
  }

  return { id: currentId, path: `/${built.join("/")}` };
}

/**
 * ✅ Cria a pasta do case dentro de /DEPARTAMENTOS/LABORATORIO
 */
export async function createCaseFolderInLab(
  accessToken: string,
  caseFolderName: string
): Promise<{ existed: boolean; name: string; fullPath: string }> {
  const { id: laboratorioId, path } = await ensurePathInRoot(accessToken, [
    "DEPARTAMENTOS",
    "LABORATORIO",
  ]);

  const folderName = safeFolderName(caseFolderName);

  const createUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${laboratorioId}/children`;
  const { res, data } = await graphJson(accessToken, createUrl, {
    method: "POST",
    body: JSON.stringify({
      name: folderName,
      folder: {},
      "@microsoft.graph.conflictBehavior": "fail",
    }),
  });

  if (!res.ok) {
    const e = data as GraphErr;
    if (e?.error?.code === "nameAlreadyExists") {
      return {
        existed: true,
        name: folderName,
        fullPath: `${path}/${folderName}`,
      };
    }
    throw new Error(e?.error?.message || `Graph HTTP ${res.status}`);
  }

  return {
    existed: false,
    name: data?.name || folderName,
    fullPath: `${path}/${data?.name || folderName}`,
  };
}
